import { useState } from "react";
import { handlePromise } from "../../../utils/handlePromisse";
import { generatePdfForConsultations, openHtmlForPrint } from "../../../utils/pdf.util";
import { duplicateOralConsultation, duplicateTopicConsultation } from "../../../services/decisionFlow";
import { Consultation } from "../../../services/patient.service";
import { DuplicatePatientState, initialState } from "./modals/duplicate/duplicate-consult.dialog";
import { HistoryConsultation } from "./history-filters";

type Params = {
    reload: () => Promise<void> | void;
    deleteConsultation: (item: HistoryConsultation) => Promise<{ ok: boolean; message?: string }>;
    navigateGated: (path: string, state?: unknown) => void;
};

/** Estado dos diálogos (PDF/duplicar/excluir) e handlers de ações do histórico. */
export function useHistoryActions({ reload, deleteConsultation, navigateGated }: Params) {
    // PDF conflict dialog state
    const [conflictDialog, setConflictDialog] = useState<{
        show: boolean;
        message: string;
        consults: Consultation[];
    }>({ show: false, message: "", consults: [] });

    // Duplicate dialog state
    const [duplicateConsultDialog, setDuplicateConsultDialog] = useState(initialState);

    // Delete dialog state
    const [deleteTarget, setDeleteTarget] = useState<HistoryConsultation | null>(null);
    const [deleteConsultDialog, setDeleteConsultDialog] = useState(false);

    // Snackbar
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    // PDF loading overlay
    const [pdfLoading, setPdfLoading] = useState(false);

    const handlePdfGenerate = async (consults: Consultation[], skipe: boolean = false) => {
        setConflictDialog({ show: false, message: "", consults: [] });
        setPdfLoading(true);

        const toGenerate = consults.map((value) => ({
            consultationId: value.id,
            type: value.type,
        }));

        const result = await generatePdfForConsultations(toGenerate);

        if (!result.success) {
            if (result.hasConflict) {
                setConflictDialog({ show: true, message: result.message || "", consults });
            } else {
                setSnackbarMessage(result.error || "Erro ao gerar PDF");
                setShowSnackbar(true);
            }
            setPdfLoading(false);
            return;
        }

        // Se retornou HTML (V3), gera o PDF no frontend
        if (result.html) {
            openHtmlForPrint(result.html);
        }
        // Se não retornou HTML (V2), o PDF já foi baixado pela função

        setPdfLoading(false);
    };

    const handleRecipe = (item: HistoryConsultation) => {
        handlePdfGenerate([{ id: item.id, date: item.date, name: item.focus, type: item.type }]);
    };

    const handleDuplicate = (item: HistoryConsultation) => {
        setDuplicateConsultDialog({
            open: true,
            consult: { id: item.id, date: item.date, name: item.focus, type: item.type },
        });
    };

    const onDuplicate = async (consultationId: number, patients: DuplicatePatientState[], type: string) => {
        setPdfLoading(true);

        let error: any;
        if (type === "ORAL") {
            [, error] = await handlePromise(duplicateOralConsultation(consultationId, patients.map((value) => value.id)));
        } else {
            [, error] = await handlePromise(duplicateTopicConsultation(consultationId, patients.map((value) => value.id)));
        }

        if (!!error) {
            setShowSnackbar(true);
            setSnackbarMessage(error.response?.data?.message || "Erro ao duplicar");
            setPdfLoading(false);
            return;
        }

        setShowSnackbar(true);
        setSnackbarMessage("Consulta duplicada com sucesso!");
        await reload();
        setPdfLoading(false);
    };

    const handleAskDelete = (item: HistoryConsultation) => {
        setDeleteTarget(item);
        setDeleteConsultDialog(true);
    };

    const handleConfirmDelete = async (id: number, type: string) => {
        if (!deleteTarget) return;
        setDeleteConsultDialog(false);
        const result = await deleteConsultation(deleteTarget);
        if (!result.ok) {
            setSnackbarMessage(result.message || "Erro ao excluir consulta.");
            setShowSnackbar(true);
        } else {
            setSnackbarMessage("Consulta removida com sucesso!");
            setShowSnackbar(true);
        }
        setDeleteTarget(null);
    };

    const openConsultation = (row: HistoryConsultation) => {
        const state = { patientName: row.patientName, focus: row.focus, date: row.date, type: row.type };
        if (row.type === "ORAL") {
            navigateGated(`/dashboard/reviewOral/${row.id}`, state);
        } else {
            navigateGated(`/dashboard/reviewConsult/${row.id}`, state);
        }
    };

    const editConsultation = (row: HistoryConsultation) => {
        const state = row.type === "ORAL"
            ? { consultationId: row.id, type: "oral" }
            : { consultationId: row.id };
        navigateGated("/dashboard/query", state);
    };

    return {
        conflictDialog,
        setConflictDialog,
        duplicateConsultDialog,
        setDuplicateConsultDialog,
        deleteTarget,
        setDeleteTarget,
        deleteConsultDialog,
        setDeleteConsultDialog,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        pdfLoading,
        handlePdfGenerate,
        handleRecipe,
        handleDuplicate,
        onDuplicate,
        handleAskDelete,
        handleConfirmDelete,
        openConsultation,
        editConsultation,
    };
}
